import MobileTabsHeader from 'components/TabsHeader/MobileTabsHeader';
import TabsHeader from 'components/TabsHeader/TabsHeader';
import { usePerms } from 'contexts/PermsContext';
import { GetPermListForRole } from 'data/permData';
import GetWidgetData from 'data/widgetData/widgetData.js';
import React, { useEffect, useRef, useState } from 'react';
import { Button, Container } from 'react-bootstrap';
import { IconContext } from 'react-icons';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGetAllAvailableDeviceTypesQuery } from 'services/devices/devices.api';
import { getWorkplaceMembers } from 'services/organization/organization.service';
import { getGlobalWidgetPermList } from 'services/perms/perms.service';
import { createRole } from 'services/roles/roles.service';
import { getVisibleUsers } from 'utils/roleUtils';
import '../Dashboard/assets/Dashboard.css';
import DashboardEditTab from './Components/DashboardEditTab';
import DashboardViewTab from './Components/DashboardViewTab';
import DetailsTab from './Components/DetailsTab';
import PermissionsTab from './Components/PermissionsTab';

const CREATE_ROLE_FORM_KEY = 'fusionCreateRoleForm';
const CREATE_ROLE_MEMBER_KEY = 'fusionCreateRoleMembers';
const CREATE_ROLE_TAB_KEY = 'fusionCreateRoleTab';
const TAB_OPTIONS = ['Role Details', 'Permissions', 'Dashboard Edit', 'Dashboard View'];

const safeJSONParse = (value, fallback) => {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch (error) {
    console.error('Failed to parse stored Create Role data', error);
    return fallback;
  }
};

const normalizeDeviceTypes = (payload) => {
  if (!payload || typeof payload !== 'object') return {};

  if (payload.status === 200 && payload.data?.result && typeof payload.data.result === 'object') {
    return payload.data.result;
  }

  if (payload.result && typeof payload.result === 'object' && !Array.isArray(payload.result)) {
    return payload.result;
  }

  return payload;
};

export default function CreateRole() {
  const navigate = useNavigate();
  const { data: deviceTypes, isLoading: deviceTypesLoading } = useGetAllAvailableDeviceTypesQuery();
  const [permList, setPermList] = useState([]);
  const [widgetData, setWidgetData] = useState(GetWidgetData());
  const [searchParams, setSearchParams] = useSearchParams();
  const tabHeaderOptions = TAB_OPTIONS.map((option) => ({ option }));
  const [formData, setFormData] = useState({
    roleName: '',
    members: [],
    initialMembers: [],
    permissions: permList.map(() => false),
    layout: [],
  });
  const [checks, setChecks] = useState({ roleName: true });
  const [result, setResult] = useState('');
  const [membersList, setMembersList] = useState([]);
  const perms = usePerms();
  const hasHydratedForm = useRef(false);
  const hasHydratedMembers = useRef(false);
  const hasInitializedTab = useRef(false);

  useEffect(() => {
    (async () => {
      if (deviceTypesLoading) return;

      const globalWidgetPermsRes = await getGlobalWidgetPermList();
      const resolvedDeviceTypes = normalizeDeviceTypes(deviceTypes);

      const deviceTypeArr = Object.keys(resolvedDeviceTypes || {}).flatMap((systemType) => {
        const systemTypeMap = resolvedDeviceTypes[systemType] || {};
        return Object.keys(systemTypeMap).flatMap((deviceType) => {
          const deviceTypeMap = systemTypeMap[deviceType] || {};
          if (
            Object.keys(deviceTypeMap).length > 0 &&
            !Object.keys(deviceTypeMap).includes('validDevices')
          )
            return Object.keys(deviceTypeMap).map(
              (subType) => systemType + ' ' + deviceType + '_' + subType,
            );
          else return systemType + ' ' + deviceType;
        });
      });

      const globalWidgetPermList = globalWidgetPermsRes?.data?.result;
      const hasGlobalPermList = Array.isArray(globalWidgetPermList?.permissions);

      const newPerms = GetPermListForRole(deviceTypeArr).map((perm, index) => ({
        ...perm,
        originalIndex: index,
        originalLinked: perm.linked || 0,
        originalEnableLinked: perm.enableLinked || 0,
      }));

      const normalizePermName = (permName) =>
        permName.replace(/\s/g, '').replace(/\//g, '-').replace(/:/g, '_');

      const filteredPerms = hasGlobalPermList
        ? newPerms.filter((filter, index) => {
            if (filter.linked > 0) {
              if (!newPerms[index + 1]) return false;
              return globalWidgetPermList.permissions.includes(
                normalizePermName(newPerms[index + 1].name),
              );
            }
            return globalWidgetPermList.permissions.includes(normalizePermName(filter.name));
          })
        : newPerms;

      const getRemainingLinkedCount = (perm, rangeLength) => {
        if (!rangeLength) return 0;
        const start = perm.originalIndex;
        const end = perm.originalIndex + rangeLength;
        return filteredPerms.reduce((count, item) => {
          if (item.originalIndex > start && item.originalIndex <= end) return count + 1;
          return count;
        }, 0);
      };

      const nextPermList = filteredPerms.map((perm, index) => ({
        ...perm,
        linked: getRemainingLinkedCount(perm, perm.originalLinked),
        enableLinked: perm.originalEnableLinked
          ? getRemainingLinkedCount(perm, perm.originalEnableLinked)
          : perm.enableLinked,
        index,
      }));

      setPermList(nextPermList);

      const allWidgetData = GetWidgetData();
      if (Array.isArray(globalWidgetPermList?.widgetList)) {
        let newWidgetList = {};
        Object.keys(allWidgetData).forEach((key) => {
          if (globalWidgetPermList.widgetList.includes(parseInt(key))) {
            newWidgetList = { ...newWidgetList, [key]: allWidgetData[key] };
          }
        });
        setWidgetData(newWidgetList);
      } else {
        setWidgetData(allWidgetData);
      }
    })();
  }, [deviceTypes, deviceTypesLoading]);

  useEffect(() => {
    (async () => {
      if (!(await perms.validatePermissions(['CreateRole']))) {
        navigate('/404');
        return;
      }

      getWorkplaceMembers(getVisibleUsers(perms)).then((res) => {
        const temp = [...res.data.result];
        const storedSelections =
          typeof window !== 'undefined'
            ? safeJSONParse(localStorage.getItem(CREATE_ROLE_MEMBER_KEY), [])
            : [];
        const selectedSet = new Set(storedSelections);
        const hydratedMembers = temp.map((item) => ({
          ...item,
          selected: selectedSet.has(item.userId),
        }));
        hasHydratedMembers.current = true;
        setMembersList([...hydratedMembers]);
      });
    })();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!hasHydratedMembers.current || typeof window === 'undefined' || !Array.isArray(membersList))
      return;

    const selectedMembers = membersList
      .filter((member) => member.selected)
      .map((member) => member.userId);

    localStorage.setItem(CREATE_ROLE_MEMBER_KEY, JSON.stringify(selectedMembers));
  }, [membersList]);

  useEffect(() => {
    if (!hasHydratedForm.current || typeof window === 'undefined') return;

    const payload = {
      roleName: formData.roleName,
      layout: formData.layout,
      permissions: formData.permissions,
    };

    localStorage.setItem(CREATE_ROLE_FORM_KEY, JSON.stringify(payload));
  }, [formData.roleName, formData.layout, formData.permissions]);

  useEffect(() => {
    if (!permList.length || hasHydratedForm.current) return;

    const storedForm =
      typeof window !== 'undefined'
        ? safeJSONParse(localStorage.getItem(CREATE_ROLE_FORM_KEY), null)
        : null;

    if (storedForm) {
      setFormData((prev) => ({
        ...prev,
        roleName: storedForm.roleName || '',
        layout: storedForm.layout || [],
        permissions: permList.map((_, index) => storedForm.permissions?.[index] ?? false),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        permissions: permList.map(() => false),
      }));
    }

    hasHydratedForm.current = true;
  }, [permList]);

  const getCurrentTabIndex = () => {
    const tabParam = searchParams.get('tab');
    const matchedIndex = TAB_OPTIONS.findIndex(
      (option) => option.toLowerCase() === tabParam?.toLowerCase(),
    );
    return matchedIndex === -1 ? 0 : matchedIndex;
  };

  const currentTabIndex = getCurrentTabIndex();

  const updateTabParam = (nextIndex, replace = false) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', TAB_OPTIONS[nextIndex]);
    setSearchParams(params, { replace });
  };

  useEffect(() => {
    if (hasInitializedTab.current) return;
    hasInitializedTab.current = true;

    if (typeof window === 'undefined') return;

    const storedTab = localStorage.getItem(CREATE_ROLE_TAB_KEY);
    const storedIndex = storedTab
      ? TAB_OPTIONS.findIndex((option) => option.toLowerCase() === storedTab.toLowerCase())
      : -1;

    if (storedIndex !== -1) {
      updateTabParam(storedIndex, true);
    } else if (!searchParams.get('tab')) {
      updateTabParam(0, true);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    localStorage.setItem(CREATE_ROLE_TAB_KEY, TAB_OPTIONS[currentTabIndex]);
  }, [currentTabIndex]);

  const clearStoredCreateRoleData = ({ clearTab = true } = {}) => {
    if (typeof window === 'undefined') return;

    localStorage.removeItem(CREATE_ROLE_FORM_KEY);
    localStorage.removeItem(CREATE_ROLE_MEMBER_KEY);
    if (clearTab) localStorage.removeItem(CREATE_ROLE_TAB_KEY);
  };

  useEffect(() => {
    return () => clearStoredCreateRoleData();
  }, []);

  const handleClearForm = () => {
    clearStoredCreateRoleData({ clearTab: false });
    setFormData((prev) => ({
      ...prev,
      roleName: '',
      layout: [],
      permissions: permList.map(() => false),
    }));
    setChecks({ roleName: true });
    setMembersList((prev) => prev.map((member) => ({ ...member, selected: false })));
    setResult('');
  };

  const renderTab = () => {
    switch (currentTabIndex) {
      case 0:
        return (
          <DetailsTab
            formData={formData}
            setFormData={setFormData}
            checks={checks}
            membersList={membersList}
            setMembersList={setMembersList}
            widgetData={widgetData}
            permList={permList}
            onClearForm={handleClearForm}
          />
        );
      case 1:
        return (
          <PermissionsTab
            formData={formData}
            setFormData={setFormData}
            checks={checks}
            widgetData={widgetData}
            permList={permList}
          />
        );
      case 2:
        return (
          <DashboardEditTab
            formData={formData}
            setFormData={setFormData}
            checks={checks}
            widgetData={widgetData}
            permList={permList}
            compactPreview
          />
        );
      case 3:
        return (
          <DashboardViewTab
            formData={formData}
            setFormData={setFormData}
            checks={checks}
            widgetData={widgetData}
            permList={permList}
            compactPreview
          />
        );
      default:
        return (
          <DetailsTab
            formData={formData}
            setFormData={setFormData}
            checks={checks}
            membersList={membersList}
            setMembersList={setMembersList}
            widgetData={widgetData}
            permList={permList}
            onClearForm={handleClearForm}
          />
        );
    }
  };

  const onSubmit = () => {
    let permissions = {};

    formData.permissions.forEach((perm, index) => {
      const permName = permList[index].name
        .replace(/\s/g, '')
        .replace(/\//g, '-')
        .replace(/:/g, '_');
      permissions = { ...permissions, [permName]: perm };
    });

    const members = [];
    membersList.forEach((member) => {
      if (member.selected) members.push(member.userId);
    });

    createRole(
      formData.roleName,
      members,
      JSON.stringify(permissions),
      JSON.stringify(formData.layout),
    )
      .then((res) => {
        clearStoredCreateRoleData();
        setResult({ type: 'success', message: 'Role created successfully' });
        navigate('/system/rolemanager');
      })
      .catch((e) => {
        setResult({
          type: 'failed',
          message: 'Failed: ' + e.response.data.message,
        });
      });
  };

  const onClose = () => {
    clearStoredCreateRoleData();
    navigate('/system/rolemanager');
  };

  const onSubmitClick = () => {
    const roleNameValid = formData.roleName !== '';
    setChecks({ roleName: roleNameValid });
    if (roleNameValid) {
      onSubmit();
    }
  };

  const onBack = () => {
    if (currentTabIndex === 0) onClose();
    else updateTabParam(currentTabIndex - 1);
  };

  const onNext = () => {
    if (currentTabIndex === TAB_OPTIONS.length - 1) {
      onSubmitClick();
    } else {
      updateTabParam(currentTabIndex + 1);
    }
  };

  return (
    <Container fluid className="userselect-none d-flex flex-column h-100">
      <div className="d-flex flex-row justify-content-between mt-2 flex-shrink-0">
        <Button
          className="d-flex flex-row align-items-center bg-primary rounded-1"
          onClick={onBack}
        >
          <div className="d-flex align-items-center">
            <IconContext.Provider value={{ size: 20 }}>
              <FaChevronLeft />
            </IconContext.Provider>
          </div>
          <div className="me-1 fs-6 gap-2">{currentTabIndex === 0 ? 'Return' : 'Back'}</div>
        </Button>
        <Button
          className="d-flex flex-row align-items-center bg-primary rounded-1"
          onClick={onNext}
        >
          <div className="ms-1 fs-6 gap-2">
            {currentTabIndex === TAB_OPTIONS.length - 1 ? 'Submit' : 'Next'}
          </div>
          {currentTabIndex !== TAB_OPTIONS.length - 1 && (
            <div className="d-flex align-items-center">
              <IconContext.Provider value={{ size: 20 }}>
                <FaChevronRight />
              </IconContext.Provider>
            </div>
          )}
        </Button>
      </div>
      <div className="mt-3 d-none d-lg-block flex-shrink-0">
        <TabsHeader options={tabHeaderOptions} />
      </div>
      <div className="mt-3 d-block d-lg-none flex-shrink-0">
        <MobileTabsHeader options={tabHeaderOptions} />
      </div>
      <div className="d-flex flex-column flex-grow-1 overflow-hidden mt-3" style={{ minHeight: 0 }}>
        <div className="d-flex bg-base flex-grow-1 overflow-hidden" style={{ minHeight: 0 }}>
          {renderTab()}
        </div>
      </div>
      {result && (
        <div
          className="fw-bold h4 flex-shrink-0"
          style={{
            color: result.type === 'success' ? 'green' : 'red',
          }}
        >
          {result.message}
        </div>
      )}
    </Container>
  );
}
